import { useCallback, useMemo, useState } from "react"
import {
  createFileRoute,
  redirect,
  useNavigate,
  useParams,
} from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { MessageSquarePlus, Plus } from "lucide-react"
import { DmThread } from "@/components/conversation/DmThread"
import { UnreadCount, UnreadDot } from "@/components/ui/UnreadMark"
import { NewDmDraft } from "@/components/conversation/NewDmDialog"
import { ThreadPanel } from "@/components/message/ThreadPanel"
import type { ColumnSpec } from "@/lib/column-layout"
import {
  parseRouteFocus,
  RoutedCollectionColumns,
  useRouteRecordSelection,
} from "@/lib/routed-collection-layout"
import { ConversationListView } from "@/components/messaging/ConversationListView"
import { getSessionUser } from "@/core/auth"
import { resolveEventId, useActiveEvent } from "@/core/active-event"
import {
  conversationsQueryOptions,
  conversationMessagesQueryOptions,
  dmTitle,
  isGroupDm,
  otherParticipants,
  previewText,
  type ConversationItem,
} from "@/core/conversations"
import { Avatar } from "@/components/ui/Avatar"
import { relTime } from "@/core/channels"

// The Conversations (DMs) LAYOUT route. Route DEPTH = Main columns:
//   /conversations                    → col 1 (DM list)
//   /conversations/$conversationId    → col 1 + 2 (+ DM thread)
// `$conversationId` lives on a MARKER child route; ColumnLayout decides how many
// fit. `?focus=2` fullscreens col 2. Legacy `?dm=…` redirects into the path.
export const Route = createFileRoute("/$orgSlug/$eventSlug/conversations")({
  validateSearch: (s: Record<string, unknown>): { focus?: number; dm?: string } => {
    return {
      focus: parseRouteFocus(s.focus),
      dm: typeof s.dm === "string" ? s.dm : undefined,
    }
  },
  beforeLoad: ({ params, search }) => {
    if (search.dm) {
      throw redirect({
        to: "/$orgSlug/$eventSlug/conversations/$conversationId",
        params: { ...params, conversationId: search.dm },
        search: { focus: search.focus },
      })
    }
  },
  loader: ({ context, params }) => {
    void resolveEventId(params.orgSlug, params.eventSlug).then((eventId) => {
      if (!eventId) return
      void context.queryClient
        .ensureQueryData(conversationsQueryOptions(eventId))
        .catch(() => undefined)
    })
  },
  component: ConversationsView,
})

function ConversationsView() {
  const params = useParams({ from: "/$orgSlug/$eventSlug/conversations" })
  const { focus } = Route.useSearch()
  const navigate = useNavigate()
  const { eventId, organizationId } = useActiveEvent()
  const qc = useQueryClient()
  const currentUserId = useMemo(() => getSessionUser()?.id ?? null, [])

  // In-pane "New conversation" draft (replaces the old full-app modal).
  const [composing, setComposing] = useState(false)
  const [query, setQuery] = useState("")

  const { conversationId: selectedId, threadId } = useRouteRecordSelection("conversationId", "threadId")

  const clearSearch = useCallback(
    () => void navigate({ to: "/$orgSlug/$eventSlug/conversations", params, search: {} }),
    [navigate, params],
  )
  // Reply-thread (col 3) navigation — keyed off the open conversation.
  const openThread = useCallback(
    (messageId: string) => {
      if (!selectedId) return
      void navigate({
        to: "/$orgSlug/$eventSlug/conversations/$conversationId/thread/$threadId",
        params: { ...params, conversationId: selectedId, threadId: messageId },
        search: {},
      })
    },
    [navigate, params, selectedId],
  )
  const closeThread = useCallback(() => {
    if (!selectedId) return
    void navigate({
      to: "/$orgSlug/$eventSlug/conversations/$conversationId",
      params: { ...params, conversationId: selectedId },
      search: {},
    })
  }, [navigate, params, selectedId])

  const open = useCallback(
    (id: string) => {
      setComposing(false)
      void navigate({
        to: "/$orgSlug/$eventSlug/conversations/$conversationId",
        params: { ...params, conversationId: id },
        search: {},
      })
    },
    [navigate, params],
  )
  const closePane = useCallback(() => {
    setComposing(false)
    clearSearch()
  }, [clearSearch])

  const dmsQ = useQuery({
    ...conversationsQueryOptions(eventId ?? ""),
    enabled: !!eventId,
  })
  const dms = dmsQ.data ?? []

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return dms
    return dms.filter((c) => c.participants.map((p) => p.displayName).join(" ").toLowerCase().includes(q))
  }, [dms, query, currentUserId])

  const selected = useMemo(
    () => dms.find((c) => c.id === selectedId) ?? null,
    [dms, selectedId],
  )
  const startCompose = useCallback(() => {
    setComposing(true)
    if (selectedId) clearSearch()
  }, [selectedId, clearSearch])

  const list = (
    <ConversationListView
      title="Direct messages"
      newLabel="New message"
      newIcon={<MessageSquarePlus className="h-[18px] w-[18px]" />}
      onNew={startCompose}
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Find a conversation…"
      rows={filtered}
      getRowKey={(c) => c.id}
      loading={dmsQ.isLoading}
      renderRow={(c) => (
        <DmListRow
          conversation={c}
          currentUserId={currentUserId}
          active={!composing && c.id === selectedId}
          onOpen={() => open(c.id)}
          onPrefetch={() => {
            if (!eventId) return
            void qc.prefetchQuery(conversationMessagesQueryOptions(eventId, c.id)).catch(() => undefined)
          }}
        />
      )}
      empty={<EmptyState hasAny={dms.length > 0} onNew={startCompose} />}
    />
  )

  const columns = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [{ id: "list", minWidth: 320, width: 340, node: list }]
    // Col 2 = compose draft (inline, no route) | selected DM thread.
    if (composing && eventId) {
      cols.push({
        id: "thread",
        minWidth: 400,
        fill: true,
        node: (
          <NewDmDraft
            eventId={eventId}
            organizationId={organizationId}
            onClose={closePane}
            onCreated={(conv) => open(conv.id)}
          />
        ),
      })
    } else if (selected) {
      cols.push({
        id: "thread",
        minWidth: 400,
        fill: true,
        node: (
          <DmThread
            key={selected.id}
            conversation={selected}
            onBack={closePane}
            closeVariant="close"
            onOpenThread={openThread}
          />
        ),
      })
      if (threadId && eventId) {
        cols.push({
          id: "reply-thread",
          minWidth: 360,
          width: 400,
          node: (
            <ThreadPanel
              key={threadId}
              eventId={eventId}
              conversationId={selected.id}
              parentMessageId={threadId}
              surfaceKind="dm"
              open
              onClose={closeThread}
              variant="panel"
            />
          ),
        })
      }
    }
    return cols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, composing, selected, eventId, organizationId, closePane, open, openThread, threadId, closeThread])

  return <RoutedCollectionColumns columns={columns} focus={focus} />
}

function DmListRow({
  conversation,
  currentUserId,
  active,
  onOpen,
  onPrefetch,
}: {
  conversation: ConversationItem
  currentUserId: string | null
  active: boolean
  onOpen: () => void
  onPrefetch?: () => void
}) {
  const others = otherParticipants(conversation, currentUserId)
  const group = isGroupDm(conversation, currentUserId)
  const title = dmTitle(conversation, currentUserId)
  const unread = conversation.unread > 0
  const stamp = conversation.lastMessage?.createdAt
    ? relTime(conversation.lastMessage.createdAt)
    : ""

  return (
    <button
      onClick={onOpen}
      onPointerEnter={() => {
        if (active) return
        onPrefetch?.()
      }}
      className={
        "group flex w-full items-center gap-3 px-3 py-2 text-left transition " +
        (active
          ? "bg-plum-100/70 dark:bg-white/10"
          : "hover:bg-plum-100/50 dark:hover:bg-white/5")
      }
    >
      <UnreadDot show={unread} reserve />
      <Avatars people={others} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span
            className={
              "min-w-0 flex-1 truncate text-[13.5px] " +
              (unread
                ? "font-bold text-plum-900 dark:text-plum-50"
                : "font-semibold text-plum-900 dark:text-plum-50")
            }
          >
            {title}
          </span>
          {group && (
            <span className="shrink-0 rounded bg-plum-100 px-1 text-[9px] font-semibold tracking-wide text-plum-700/60 uppercase dark:bg-white/10 dark:text-plum-200/45">
              Group
            </span>
          )}
          {stamp && (
            <span className="shrink-0 text-[10.5px] text-plum-700/45 dark:text-plum-200/35">
              {stamp}
            </span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-2">
          <span
            className={
              "min-w-0 flex-1 truncate text-[12px] " +
              (unread
                ? "font-medium text-plum-800/80 dark:text-plum-100/70"
                : "text-plum-700/55 dark:text-plum-200/40")
            }
          >
            {previewText(conversation.lastMessage)}
          </span>
          <UnreadCount n={conversation.unread} />
        </span>
      </span>
    </button>
  )
}

// 1:1 → one avatar; group → two stacked avatars (Slack's group-DM tile).
function Avatars({
  people,
}: {
  people: { userId: string; displayName: string }[]
}) {
  if (people.length <= 1) {
    const p = people[0]
    return (
      <span className="relative shrink-0">
        <Avatar
          name={p?.displayName ?? "?"}
          kind="organizer"
          userId={p?.userId ?? "x"}
          size={36}
        />
      </span>
    )
  }
  const [a, b] = people
  return (
    <span className="relative h-9 w-9 shrink-0">
      <Avatar
        name={a.displayName}
        kind="organizer"
        userId={a.userId}
        size={24}
        className="absolute top-0 left-0 ring-2 ring-surface"
      />
      <Avatar
        name={b.displayName}
        kind="organizer"
        userId={b.userId}
        size={24}
        className="absolute right-0 bottom-0 ring-2 ring-surface"
      />
    </span>
  )
}

function EmptyState({ hasAny, onNew }: { hasAny: boolean; onNew: () => void }) {
  return (
    <div className="flex flex-col items-center px-6 py-16 text-center">
      <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-plum-100/70 text-plum-700/70 dark:bg-white/5 dark:text-plum-200/55">
        <MessageSquarePlus className="h-5 w-5" />
      </div>
      <h2 className="text-[15px] font-semibold text-plum-900 dark:text-plum-50">
        {hasAny ? "No matches" : "No direct messages yet"}
      </h2>
      <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-plum-700/55 dark:text-plum-200/45">
        {hasAny
          ? "Try a different name."
          : "Start a private conversation with a teammate or an attendee."}
      </p>
      {!hasAny && (
        <button
          onClick={onNew}
          className="mt-4 flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-semibold text-white shadow-fluent-sm transition hover:brightness-110"
          style={{ background: "var(--accent)" }}
        >
          <Plus className="h-4 w-4" />
          New message
        </button>
      )}
    </div>
  )
}
